import { alertsReducer } from './alerts';
import { executionsReducer } from './executions';
import { locatesReducer } from './locates';
import { locatesV2Reducer } from './locatesV2';
import { messagesReducer } from './messages';
import { orderBlotterReducer } from './orderBlotter';
import { ordersReducer } from './orders';
import { positionsReducer } from './positions';
import { scannerReducer } from './scanner';
import { screenerReducer } from './screener';
import { searchAlphaReducer } from './searchAlphaReducer';
import { tradeProReducer } from './tradePro';
import { watchlistReducer } from './watchlist';
import type { AppAction } from 'src/store/actions';
import type { UpdateTabNameAction } from 'src/store/actions/tab';
import type { OnTabChannelChangeEvent, UpdateWidgetAction } from 'src/store/actions/widgetAndChannel';
import { combineReducersFlat } from 'src/store/lib/combineReducersFlat';
import type { WidgetTabViewModel, WidgetViewModel } from 'src/store/types';

type TargetedWidgetUpdateAction = UpdateTabNameAction | UpdateWidgetAction | OnTabChannelChangeEvent;

const targetedWidgetReducer = (state: WidgetTabViewModel, action: TargetedWidgetUpdateAction): WidgetTabViewModel => {
    switch (action.type) {
        case 'updateTabName':
            return {
                ...state,
                name: action.tabName,
            };

        case 'updateWidget':
            return {
                ...state,
                ...(action.tabUpdates ? action.tabUpdates : {}),
                widget: { ...state.widget, ...action.widgetUpdates } as WidgetViewModel,
            };

        case 'onTabChannelChange':
            return {
                ...state,
                channelId: action.channelId ? action.channelId : null,
                widget: { ...state.widget, channelId: action.channelId },
            };

        default:
            return state;
    }
};

const deferredIdeaHydrationReducer = (state: WidgetTabViewModel, action: AppAction): WidgetTabViewModel => {
    switch (action.type) {
        case 'setDeferredWidgetIdeaHydrationStatus': {
            if (action.widgetType !== state.widget.type) return state;

            switch (state.widget.type) {
                case 'searchalpha': {
                    if (!state.widget.ideaHydration || state.widget.ideaHydration.status === 'ready') return state;
                    return {
                        ...state,
                        widget: {
                            ...state.widget,
                            ideaHydration: { ...state.widget.ideaHydration, status: action.status },
                        },
                    };
                }
                case 'scanner': {
                    if (!state.widget.ideaHydration || state.widget.ideaHydration.status === 'ready') return state;
                    return {
                        ...state,
                        widget: {
                            ...state.widget,
                            ideaHydration: { ...state.widget.ideaHydration, status: action.status },
                        },
                    };
                }
                case 'screener': {
                    if (!state.widget.ideaHydration || state.widget.ideaHydration.status === 'ready') return state;
                    return {
                        ...state,
                        widget: {
                            ...state.widget,
                            ideaHydration: { ...state.widget.ideaHydration, status: action.status },
                        },
                    };
                }
                case 'watchlist': {
                    if (!state.widget.ideaHydration || state.widget.ideaHydration.status === 'ready') return state;
                    return {
                        ...state,
                        widget: {
                            ...state.widget,
                            ideaHydration: { ...state.widget.ideaHydration, status: action.status },
                        },
                    };
                }
                default:
                    return state;
            }
        }

        case 'hydrateDeferredWidgetIdea': {
            if (action.widgetType === 'searchalpha' && state.widget.type === 'searchalpha') {
                return {
                    ...state,
                    widget: {
                        ...state.widget,
                        idea: action.idea,
                        isIdeaDirty: action.isIdeaDirty,
                        ideaHydration: { status: 'ready' },
                    },
                };
            }

            if (action.widgetType === 'scanner' && state.widget.type === 'scanner') {
                return {
                    ...state,
                    widget: {
                        ...state.widget,
                        idea: action.idea,
                        isIdeaDirty: action.isIdeaDirty,
                        defaultIdeaStatus: 'loaded',
                        ideaHydration: { status: 'ready' },
                    },
                };
            }

            if (action.widgetType === 'screener' && state.widget.type === 'screener') {
                return {
                    ...state,
                    widget: {
                        ...state.widget,
                        idea: action.idea,
                        isIdeaDirty: action.isIdeaDirty,
                        ideaHydration: { status: 'ready' },
                    },
                };
            }

            if (action.widgetType === 'watchlist' && state.widget.type === 'watchlist') {
                return {
                    ...state,
                    widget: {
                        ...state.widget,
                        idea: action.idea,
                        isIdeaDirty: action.isIdeaDirty,
                        defaultIdeaStatus: 'loaded',
                        ideaHydration: { status: 'ready' },
                    },
                };
            }

            return state;
        }

        default:
            return state;
    }
};

export const tabIdReducer = (state: WidgetTabViewModel, action: AppAction): WidgetTabViewModel => {
    if (!('tabId' in action)) return state;
    if (action.tabId !== state.id) return state;

    return combineReducersFlat<WidgetTabViewModel>([
        targetedWidgetReducer,
        deferredIdeaHydrationReducer,
        alertsReducer,
        executionsReducer,
        locatesReducer,
        locatesV2Reducer,
        messagesReducer,
        orderBlotterReducer,
        tradeProReducer,
        ordersReducer,
        positionsReducer,
        scannerReducer,
        screenerReducer,
        searchAlphaReducer,
        watchlistReducer,
    ])(state, action);
};
